import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MainView = 'chat' | 'settings' | 'help' | 'search' | 'terms';
export type Theme = 'light' | 'dark' | 'system';
export type FontSize = 'small' | 'medium' | 'large' | 'xlarge';

const fontSizeValues: Record<FontSize, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
  xlarge: '20px',
};

interface UIState {
  sidebarOpen: boolean;
  currentView: MainView;
  theme: Theme;
  fontSize: FontSize;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setCurrentView: (view: MainView) => void;
  setTheme: (theme: Theme) => void;
  setFontSize: (size: FontSize) => void;
}

// Apply theme to document
function applyTheme(theme: Theme) {
  const root = document.documentElement;

  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

// Apply font size to document
function applyFontSize(size: FontSize) {
  document.documentElement.style.fontSize = fontSizeValues[size];
}

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const storedState = localStorage.getItem('ui-storage');
    if (storedState) {
      const { state } = JSON.parse(storedState);
      if (state?.theme === 'system') {
        applyTheme('system');
      }
    }
  });
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: false,
      currentView: 'chat',
      theme: 'system',
      fontSize: 'medium',
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setCurrentView: (view) => set({ currentView: view }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      setFontSize: (size) => {
        applyFontSize(size);
        set({ fontSize: size });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        theme: state.theme,
        fontSize: state.fontSize,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme on initial load
        if (state?.theme) {
          applyTheme(state.theme);
        }
        // Apply font size on initial load
        if (state?.fontSize) {
          applyFontSize(state.fontSize);
        }
      },
    }
  )
);
